import { z } from "zod";
import {
  emailSchema,
  googlePlaceOrNullSchema,
  isoDateSchema,
  pickupTimeWireSchema,
  time24OrEmptySchema,
  usPhoneSchema,
} from "./common.js";
import { mrnSchema } from "./patient.js";

// =============================================================================
// Trip enums — kept as Zod literals so the shared package can power both the
// React form's RHF resolver and the API's request validation.
// =============================================================================

export const transportTypeSchema = z.enum(["AMB", "W/C", "BWC", "STR", "BSTR"]);
export type TransportType = z.infer<typeof transportTypeSchema>;

export const orderTypeSchema = z.enum(["one_time", "standing_order"]);
export type OrderType = z.infer<typeof orderTypeSchema>;

export const tripTypeSchema = z.enum(["one_way", "round_trip", "multi_leg"]);
export type TripType = z.infer<typeof tripTypeSchema>;

/** "will_call" is the wire form for a Will Call — the user-facing toggle on
 *  the form translates into this on submit. */
export const timeTypeSchema = z.enum(["pickup", "appointment", "will_call"]);
export type TimeType = z.infer<typeof timeTypeSchema>;

export const returnTimeModeSchema = z.enum(["scheduled", "will_call"]);
export type ReturnTimeMode = z.infer<typeof returnTimeModeSchema>;

// =============================================================================
// Multi-leg
// =============================================================================

export const additionalLegSchema = z.object({
  from: z.string().max(200),
  to: z.string().max(200),
  fromCityZip: z.string().max(100),
  toCityZip: z.string().max(100),
  fromPlace: googlePlaceOrNullSchema,
  toPlace: googlePlaceOrNullSchema,
  /** "WILL_CALL" or HH:MM in 24h. */
  time: pickupTimeWireSchema,
  notes: z.string().max(500).default(""),
  willCall: z.boolean(),
});
export type AdditionalLeg = z.infer<typeof additionalLegSchema>;

// =============================================================================
// Trip create — the request body sent to /api/trip/create.
// Mirrors the payload the existing static form built (script.js submit handler)
// so the upstream triplink.ai integration keeps working unchanged.
// =============================================================================

export const tripCreateRequestSchema = z.object({
  // ---- Basic info ----------------------------------------------------------
  mrn: mrnSchema,
  costCenter: z.string().min(1).max(20),
  department: z.string().max(100).default(""),
  firstName: z.string().min(1).max(50),
  lastName: z.string().min(1).max(50),
  /** YYYY-MM-DD; must be in the past (frontend validates). */
  dob: isoDateSchema,

  orderType: orderTypeSchema,
  tripType: tripTypeSchema,
  transportType: transportTypeSchema,

  // ---- Pickup --------------------------------------------------------------
  /** YYYY-MM-DD or "" when orderType === "standing_order". */
  transportDate: z.string(),
  pickupAddress: z.string().min(1).max(200),
  /** City/state/ZIP tail. Optional at the wire level: when the booking
   *  carries coordinates (pickupPlace) the BFF sends exact lat/lon to
   *  dispatch and composes the address from the place, so the text
   *  city/zip isn't required. The web form enforces "cityZip OR
   *  coordinates" client-side. */
  pickupCityZip: z.string().max(100).default(""),
  /** Only set when the Alton Rd campus dropdown is visible. */
  pickupLocation: z.string().max(200).default(""),
  contactPhone: usPhoneSchema,

  /** "pickup" | "appointment" | "will_call". When will_call, both time fields are empty. */
  timeType: timeTypeSchema,
  pickupTimeEstimated: pickupTimeWireSchema,
  appointmentTime: time24OrEmptySchema,
  pickupNotes: z.string().max(1000).default(""),

  pickupPlace: googlePlaceOrNullSchema,

  // ---- Drop-off ------------------------------------------------------------
  destinationAddress: z.string().min(1).max(200),
  /** Optional at the wire level — same coordinate rationale as
   *  pickupCityZip. */
  destinationCityZip: z.string().max(100).default(""),
  destinationPlace: googlePlaceOrNullSchema,
  dropoffNotes: z.string().max(1000).default(""),

  // ---- Round trip ----------------------------------------------------------
  returnTimeMode: z.union([returnTimeModeSchema, z.literal("")]).default(""),
  returnPickupTime: pickupTimeWireSchema.default(""),
  returnToOriginalPickup: z.boolean().default(true),
  returnCustomDestinationAddress: z.string().max(200).default(""),
  returnCustomDestinationCityZip: z.string().max(100).default(""),
  returnDestinationPlace: googlePlaceOrNullSchema.default(null),
  returnLegNotes: z.string().max(1000).default(""),

  // ---- Multi-leg -----------------------------------------------------------
  additionalLegs: z.array(additionalLegSchema).default([]),

  // ---- Standing order (currently disabled in the UI but kept for compat) ---
  standingDefaultPickupTime: pickupTimeWireSchema.default(""),
  standingDefaultAppointmentTime: time24OrEmptySchema.default(""),
  standingSchedule: z.array(z.unknown()).default([]),

  // ---- Requester -----------------------------------------------------------
  requestedBy: z.string().min(1).max(100),
  callbackPhone: usPhoneSchema,
  requesterEmail: emailSchema.default(""),

  // ---- Meta ---------------------------------------------------------------
  /** Server fills this in if missing; frontend sets it for parity with the old form. */
  createdAt: z.string().optional(),
  source: z.string().default("mount-sinai-trip-request-form"),

  /** Server-side use only — not submitted by the form. */
  appointmentBufferMinutes: z.number().int().nonnegative().optional(),

  /** Wall-clock milliseconds the requester spent on the form before
   *  submitting (form open → submit). Optional, set by the web form on
   *  single bookings only. Used purely for the "typical time to book"
   *  dashboard metric — recorded in the audit log, never forwarded to
   *  dispatch in a meaningful way. Clamped client-side; the server
   *  ignores out-of-range values. */
  formDurationMs: z.number().int().nonnegative().optional(),

  /** Why this booking is being made inside the minimum lead time. Only
   *  honoured for a caller holding the `bookShortNotice` permission; anyone
   *  else is refused regardless of what they send. Recorded on the
   *  booking.create audit row so a last-minute booking always carries a
   *  written reason. */
  shortNoticeReason: z.string().max(200).optional(),
});
export type TripCreateRequest = z.infer<typeof tripCreateRequestSchema>;

// =============================================================================
// Trip create — response from triplink.ai, unwrapped one level for clarity.
// =============================================================================

export const createdTripSchema = z.object({
  friendlyId: z.string(),
  leg: z.union([z.string(), z.number()]).optional(),
});
export type CreatedTrip = z.infer<typeof createdTripSchema>;

export const tripCreateResponseSchema = z.object({
  trips: z.array(createdTripSchema),
  groupId: z.string().optional().default(""),
});
export type TripCreateResponse = z.infer<typeof tripCreateResponseSchema>;

// =============================================================================
// Recurring trip create — POST /api/trip/create-recurring
//
// A nurse books the SAME trip across multiple dates at once (e.g. every
// Monday / Wednesday / Friday for six weeks). We never ask TripLink to
// understand "recurrence" — the server fans out one ordinary
// `createTrip` call per date. Each occurrence is a fully independent
// trip (its own friendlyId, and for round-trips its own A/B group),
// exactly as if it had been booked one at a time. TripLink can't tell
// the difference, so there's zero new upstream surface.
//
// The request carries the base trip payload (identical to a single
// create) plus the explicit list of dates the frontend generated from
// the weekday + range picker (and that the user could prune in the
// preview). The server overrides `transportDate` per occurrence; every
// other field — addresses, times-of-day, round-trip return details,
// transport type — is shared across all occurrences.
// =============================================================================

/** Hard ceiling on occurrences per recurring booking. Six months of a
 *  thrice-weekly schedule is ~78; we cap lower at 60 so a fat-fingered
 *  range can't fan out hundreds of upstream calls in one request. The
 *  frontend caps the generated list at the same number. */
export const MAX_RECURRING_OCCURRENCES = 60;

export const tripCreateRecurringRequestSchema = z.object({
  /** The base trip — same shape as a single create. Its `transportDate`
   *  is ignored; the server stamps each occurrence's date from `dates`
   *  below. Everything else (addresses, times, round-trip return,
   *  transport type, requester) is reused for every occurrence. */
  trip: tripCreateRequestSchema,
  /** Explicit, de-duplicated list of YYYY-MM-DD dates to book. The
   *  frontend generates these from the recurrence pattern and lets the
   *  user remove individual dates before submitting. */
  dates: z
    .array(isoDateSchema)
    .min(1, "Pick at least one date.")
    .max(
      MAX_RECURRING_OCCURRENCES,
      `Too many dates — keep it to ${MAX_RECURRING_OCCURRENCES} or fewer per booking.`,
    ),
  /** How long the caller will wait for the answer, in ms: the web form sends
   *  its own time limit. The server stops starting dates shortly before
   *  then, so the answer, with the dates it didn't send, still reaches the
   *  booker. A phone booking leaves it out and waits as long as it takes. */
  waitMs: z.number().int().positive().max(600_000).optional(),
});
export type TripCreateRecurringRequest = z.infer<
  typeof tripCreateRecurringRequestSchema
>;

/** One occurrence's outcome. Best-effort fan-out: a single date failing
 *  (e.g. a transient TripLink hiccup) doesn't abort the rest — it's
 *  reported here so the user can retry just that date. */
export const recurringOccurrenceResultSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  status: z.enum(["created", "failed"]),
  /** friendlyId(s) returned for this date — one for one-way, two (A/B)
   *  for a round-trip. Empty when the occurrence failed. */
  friendlyIds: z.array(z.string()).default([]),
  /** Human-readable failure reason, present only on `failed`. */
  error: z.string().optional(),
  /** On `failed`: dispatch never answered with a clear no, so the date may
   *  already be booked. It's checked before it's booked again, never
   *  re-sent from the result screen. */
  unconfirmed: z.boolean().optional(),
  /** On `failed`: never sent, because the booking ran out of the booker's
   *  time. Re-booked like a refused date. */
  notSent: z.boolean().optional(),
});
/** What a date dispatch never confirmed says, wherever it's listed. */
export const RECURRING_NOT_CONFIRMED =
  "Not confirmed — check Booked Trips before booking it again.";

export type RecurringOccurrenceResult = z.infer<
  typeof recurringOccurrenceResultSchema
>;

export const tripCreateRecurringResponseSchema = z.object({
  results: z.array(recurringOccurrenceResultSchema),
  createdCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
});
export type TripCreateRecurringResponse = z.infer<
  typeof tripCreateRecurringResponseSchema
>;

// =============================================================================
// ETA — Track My Trip
// =============================================================================

export const tripEtaRequestSchema = z.object({
  friendlyId: z.string().min(1),
});
export type TripEtaRequest = z.infer<typeof tripEtaRequestSchema>;

export const tripStatusSchema = z.enum([
  "draft",
  "available",
  "pending",
  "will_call",
  "assigned",
  "en_route",
  "arrived",
  "in_progress",
  "at_destination",
  "re_route",
  "finished",
  "no_show",
  "late_cancel",
  "customer_canceled",
  "customer_requested_cancel",
  "organization_canceled",
  "fleet_canceled",
  "deleted",
]);
export type TripStatus = z.infer<typeof tripStatusSchema>;

export const tripEtaResponseSchema = z.object({
  status: z.string(),
  pickupAt: z.string().optional(),
  eta: z.string().optional(),
});
export type TripEtaResponse = z.infer<typeof tripEtaResponseSchema>;

// =============================================================================
// Schedule — Booked Trips
// =============================================================================

export const tripScheduleRequestSchema = z.object({
  date: isoDateSchema,
});
export type TripScheduleRequest = z.infer<typeof tripScheduleRequestSchema>;

/** A single trip on the booked-trips list. We accept unknown additional fields
 *  in case triplink.ai grows the response — drivers, vehicles, etc. — without
 *  making us redeploy.
 *
 *  The BFF augments triplink's response with fields from our own
 *  `bookings` snapshot (mrn, lastName, requestedBy) when the
 *  triplinkFriendlyId matches, so dispatch can search by patient
 *  identifier even though triplink itself doesn't ship MRN. The
 *  enriched fields are optional because triplink may surface trips
 *  we never wrote (legacy bookings, manual entries, etc). */
export const tripScheduleItemSchema = z
  .object({
    friendlyId: z.string(),
    status: z.string(),
    patientName: z.string(),
    serviceType: z.string(),
    pickupTime: z.string(),
    /** Same moment as pickupTime but as an ISO-8601 UTC timestamp.
     *  Used by the UI to compute lead time client-side (e.g. is the trip
     *  beyond TRIP_EDIT_MIN_LEAD_HOURS? then the edit button is visible). The
     *  human-readable `pickupTime` is in NY local and can't be parsed
     *  unambiguously in the browser, hence this companion field. */
    pickupAtIso: z.string().optional(),
    /** Appointment time formatted for display ("May 15, 2026 14:00"),
     *  same layout as pickupTime. Present only on appointment-mode trips. */
    appointmentTime: z.string().optional(),
    /** Wall-clock appointment time in the dropoff timezone, no tz suffix
     *  ("2026-05-15T14:00:00"). Present only on appointment-mode trips;
     *  pickup-mode and will-call trips omit it. Frontend uses presence
     *  to decide whether the edit dialog shows the pickup-time or
     *  appointment-time field. */
    appointmentAtIso: z.string().optional(),
    /** Pickup place's raw address ("FL-7, Miami Gardens, FL, USA"),
     *  unmasked — dispatch needs to see where the patient is. */
    pickupAddress: z.string().optional(),
    /** Destination place's raw address. */
    destinationAddress: z.string().optional(),
    eta: z.string().optional(),
    /** Locally captured patient MRN (lowercased). */
    mrn: z.string().optional(),
    /** Locally captured patient last name (separate from triplink's
     *  combined `patientName` so we can search on it directly). */
    lastName: z.string().optional(),
    /** Locally captured first name. Mostly redundant with patientName
     *  but useful when the upstream patientName format drifts. */
    firstName: z.string().optional(),
    /** Locally captured "requested by" so dispatch can search by the
     *  staff member who booked the trip. */
    requestedBy: z.string().optional(),
    /** Triplink-provided patient identifier — equivalent to our `mrn`.
     *  Triplink stores Mount Sinai's MRN in this column. Used by the
     *  background sync to map schedule entries back to their patient
     *  across all booking channels (V1 form, V2 form, phone bookings). */
    fin: z.string().optional(),
    /** Triplink-provided trip creation timestamp (ISO 8601 UTC).
     *  Different from pickupAtIso — this is *when the booking was
     *  taken*, not when the pickup is scheduled. Powers the "booked
     *  4h ago" chip in the duplicate-detection panel. */
    createdAt: z.string().optional(),
    /** Patient date of birth as returned by triplink (YYYY-MM-DD).
     *  Omitted when the customer record has no DOB. */
    dob: z.string().optional(),
    /** Patient's default phones from triplink's fleet_customer_phones,
     *  digits-only, deduped, US country code stripped. Multiple entries
     *  when the customer has more than one default. */
    contactPhones: z.array(z.string()).optional(),
    /** Booking-coordinator phone parsed from the "Requested By: …" line
     *  of the trip note. Digits-only. Distinct from contactPhones -
     *  this is who placed the order, not the patient. */
    callbackPhone: z.string().optional(),
    /** GL code / cost center on the trip (fleet_trips.gl_code). */
    costCenter: z.string().optional(),
    /** Free-form booking note from triplink (fleet_trips.note).
     *  Contains structured "Department: …", "MRN: …", "Requested
     *  By: …" lines plus any inline dispatcher notes. */
    note: z.string().optional(),
    /** Trip distance in miles as computed upstream by TripLink. Present
     *  only once their mileage rollout reaches a trip; absent on older
     *  rows. Drives our local rate calculation. Tolerates number or
     *  numeric-string; null / "" are treated as absent. */
    mileage: z.preprocess(
      (v) => (v === null || v === "" ? undefined : v),
      z.coerce.number().nonnegative().optional(),
    ),
  })
  .passthrough();
export type TripScheduleItem = z.infer<typeof tripScheduleItemSchema>;

export const tripScheduleResponseSchema = z.object({
  trips: z.array(tripScheduleItemSchema),
});
export type TripScheduleResponse = z.infer<typeof tripScheduleResponseSchema>;

// =============================================================================
// Will Call activation — flips a will_call trip into "available now"
// upstream. The BFF proxies this to triplink.ai's matching endpoint
// (wired). 404s from upstream are translated to NOT_IMPLEMENTED as a
// defensive fallback so the UI degrades gracefully during outages.
// =============================================================================

export const tripActivateWillCallRequestSchema = z.object({
  friendlyId: z.string().min(1),
});
export type TripActivateWillCallRequest = z.infer<
  typeof tripActivateWillCallRequestSchema
>;

export const tripActivateWillCallResponseSchema = z.object({
  friendlyId: z.string(),
  status: z.string(),
});
export type TripActivateWillCallResponse = z.infer<
  typeof tripActivateWillCallResponseSchema
>;

// =============================================================================
// Cancel trip — only available while a trip is in `pending` /
// `assigned` / `will_call` status; the BFF guards on that and the UI
// doesn't render the button for terminal trips. Wired to triplink's
// cancel endpoint (defensive NOT_IMPLEMENTED fallback for 404 / outage,
// dry-run mock when TRIPLINK_DRY_RUN is on, same shape as activate-
// will-call).
// =============================================================================

export const tripCancelRequestSchema = z.object({
  friendlyId: z.string().min(1),
  /** Optional free-text reason captured for the audit log. Not required
   *  by triplink today; we surface it so dispatch can record context for
   *  why the cancellation happened. */
  reason: z.string().max(200).default(""),
});
export type TripCancelRequest = z.infer<typeof tripCancelRequestSchema>;

export const tripCancelResponseSchema = z.object({
  friendlyId: z.string(),
  status: z.string(),
});
export type TripCancelResponse = z.infer<typeof tripCancelResponseSchema>;

// =============================================================================
// Trip update — change pickup time and/or addresses on an existing trip.
//
// Rules:
//   • Trip must be in pending / will_call / assigned status.
//   • Existing pickup must be beyond TRIP_EDIT_MIN_LEAD_HOURS from now.
//     ENFORCED IN THE UI ONLY. This block used to say "enforced upstream;
//     UI shouldn't try to anticipate them" — that was never verified and is
//     wrong for our side: routes/trip.ts /update checks the modifyTrip
//     permission, the rate limit and this schema, and nothing else. Whether
//     TripLink applies its own floor is UNCONFIRMED (asked in docs/TRIPLINK.md).
//     If a late edit must be genuinely prevented, add the check here.
//   • At least one field must actually differ from the current trip.
//   • pickupTime and appointmentTime are mutually exclusive — send one or
//     neither. If neither, only the addresses change and times re-anchor
//     based on the trip's existing mode (pickup-anchored or appointment-
//     anchored) using the new duration.
//
// Datetime format: "YYYY-MM-DDTHH:MM" or "YYYY-MM-DD HH:MM" — wall clock in
// the pickup-place timezone, no tz offset. The backend pins the timezone
// from the saved place.
// =============================================================================

export const tripUpdateRequestSchema = z
  .object({
    friendlyId: z.string().min(1),

    pickupTime: z.string().max(32).default(""),
    appointmentTime: z.string().max(32).default(""),

    pickupAddress: z.string().max(200).default(""),
    pickupCityZip: z.string().max(100).default(""),
    pickupPlace: googlePlaceOrNullSchema.default(null),

    destinationAddress: z.string().max(200).default(""),
    destinationCityZip: z.string().max(100).default(""),
    destinationPlace: googlePlaceOrNullSchema.default(null),
  })
  .refine(
    (v) => !(v.pickupTime && v.appointmentTime),
    "Provide either pickupTime or appointmentTime, not both.",
  )
  .refine(
    // If the caller sent a new pickupAddress they must have a
    // resolved Place to go with it — otherwise upstream gets a
    // free-text line with no lat/lon and either dispatches blindly
    // or rejects with a cryptic 400. The UI's AddressField clears
    // both together, so this only fires on a malformed direct API
    // hit.
    (v) => !v.pickupAddress || v.pickupPlace !== null,
    "pickupAddress requires a resolved pickupPlace.",
  )
  .refine(
    (v) => !v.destinationAddress || v.destinationPlace !== null,
    "destinationAddress requires a resolved destinationPlace.",
  );
export type TripUpdateRequest = z.infer<typeof tripUpdateRequestSchema>;

export const tripUpdateResponseSchema = z.object({
  friendlyId: z.string(),
  status: z.string(),
  pickupAt: z.string().optional(),
  appointmentAt: z.string().optional(),
});
export type TripUpdateResponse = z.infer<typeof tripUpdateResponseSchema>;

// =============================================================================
// API envelope — every BFF response is wrapped { ok, response | error }
// so the frontend has one place to catch errors regardless of upstream shape.
// =============================================================================

export const apiSuccessSchema = <T extends z.ZodTypeAny>(inner: T) =>
  z.object({ ok: z.literal(true), response: inner });

export const apiErrorSchema = z.object({
  ok: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
